import { convertToRole, Role, roleCollectionKey } from "@/entities/role";
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import store from "@/store";
import {
  getLoginEnvironment,
  readAdminLoginLink
} from "@/utils/superAdminEmailLogin";
import type { PendingSuperAdminLogin } from "@/utils/superAdminEmailLogin";
import {
  isSchoolAiUsageExpired,
  schoolCollectionKey,
  schoolConfigCollectionKey
} from "@/entities/school";
import { isTutorInRecess, tutorConfigCollectionKey } from "@/entities/tutor";
import {
  clearStaffLoginViaSso,
  markStaffLoginViaSso
} from "@/utils/staff_login_via_sso";

export const ADMIN_SCHOOL_AI_USAGE_EXPIRED_MESSAGE =
  "このスクールの利用期限が過ぎているため、管理画面を利用できません。管理者にお問い合わせください。";

export const TUTOR_PASSWORD_LOGIN_DISABLED_MESSAGE =
  "パスワードでのログインは無効になっています。Google または Microsoft アカウントでログインしてください。";

export const ADMIN_TUTOR_IN_RECESS_MESSAGE =
  "このアカウントは現在休会中であるため、管理画面を利用できません。管理者にお問い合わせください。";

// 利用期限切れ・休会で止めたエラーか。message はそのまま画面に出せる文言
export function isAdminAccessStoppedError(error: unknown): error is Error {
  return (
    error instanceof Error &&
    (error.message === ADMIN_SCHOOL_AI_USAGE_EXPIRED_MESSAGE ||
      error.message === ADMIN_TUTOR_IN_RECESS_MESSAGE)
  );
}

export async function assertAdminAccessAllowed(
  uid: string,
  role?: Role
): Promise<Role> {
  let resolvedRole = role;
  if (!resolvedRole) {
    const roleSnapshot = await firebase
      .firestore()
      .collection(roleCollectionKey)
      .doc(uid)
      .get();
    const roleData = roleSnapshot.data();
    if (roleSnapshot.exists && roleData) {
      resolvedRole = convertToRole(roleData, roleSnapshot.ref);
    }
  }
  if (!resolvedRole) {
    throw new Error("Failed to get role data");
  }
  if (resolvedRole.data.type !== "tutor") {
    return resolvedRole;
  }

  const schoolRef = resolvedRole.data.ref.parent.parent;
  if (!schoolRef) {
    throw new Error("Failed to get school data");
  }

  const db = firebase.firestore();
  const [schoolConfigSnapshot, tutorConfigSnapshot] = await Promise.all([
    db.collection(schoolConfigCollectionKey).doc(schoolRef.id).get(),
    db
      .collection(schoolCollectionKey)
      .doc(schoolRef.id)
      .collection(tutorConfigCollectionKey)
      .doc(uid)
      .get()
  ]);

  if (isTutorInRecess(tutorConfigSnapshot.data())) {
    await firebase.auth().signOut();
    throw new Error(ADMIN_TUTOR_IN_RECESS_MESSAGE);
  }
  if (
    isSchoolAiUsageExpired(schoolConfigSnapshot.data()?.schoolAiUsageExpiresAt)
  ) {
    await firebase.auth().signOut();
    throw new Error(ADMIN_SCHOOL_AI_USAGE_EXPIRED_MESSAGE);
  }

  return resolvedRole;
}

export async function signIn(
  email: string,
  password: string
): Promise<Role | PendingSuperAdminLogin> {
  clearStaffLoginViaSso();
  const auth = firebase.auth();

  const tutorPasswordLogin = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("tutor_password_login");

  const res = await tutorPasswordLogin({
    email,
    password,
    environment: getLoginEnvironment("admin")
  });
  const data = res.data as {
    token?: string;
    emailVerificationRequired?: boolean;
    requestLabel?: string;
  };
  if (data.emailVerificationRequired) {
    return { emailVerificationRequired: true, requestLabel: data.requestLabel };
  }
  const token = data.token;
  if (!token) {
    throw new Error("Failed to sign in");
  }

  const userCredential = await auth.signInWithCustomToken(token);

  const firebaseUser = userCredential.user;
  if (!firebaseUser) {
    throw new Error("Failed to get user");
  }

  const role = await assertAdminAccessAllowed(firebaseUser.uid);

  return role;
}

let emailLoginCompletion: Promise<boolean> | undefined;
export function completeSuperAdminEmailLogin(): Promise<boolean> {
  if (emailLoginCompletion) return emailLoginCompletion;
  const link = readAdminLoginLink();
  if (!link && !window.location.hash.startsWith("#adminLogin="))
    return Promise.resolve(false);
  window.history.replaceState(
    window.history.state,
    "",
    window.location.pathname + window.location.search
  );
  emailLoginCompletion = (async () => {
    if (!link) throw new Error("確認リンクが不正です。ログインし直してください。");
    const environment = getLoginEnvironment("admin", false);
    if (!environment.browserSecret)
      throw new Error(
        "ログインを開始したブラウザで、Slackの認証リンクを開いてください。"
      );
    const complete = firebase
      .app()
      .functions("asia-northeast1")
      .httpsCallable("complete_super_admin_email_login");
    const result = await complete({ ...link, environment });
    const token = (result.data as { token?: string }).token;
    if (!token)
      throw new Error("ログイン確認に失敗しました。ログインし直してください。");
    clearStaffLoginViaSso();
    await firebase.auth().signInWithCustomToken(token);
    return true;
  })().finally(() => {
    emailLoginCompletion = undefined;
  });
  return emailLoginCompletion;
}

export async function signOut(): Promise<void> {
  try {
    store.commit("SET_SIGNING_OUT", true);
    store.commit("SET_CANCEL_STUDENT_FETCH", true);
    await firebase.auth().signOut();
  } finally {
    clearStaffLoginViaSso();
    store.commit("SET_SIGNING_OUT", false);
    store.commit("SET_CANCEL_STUDENT_FETCH", false);
  }
}

export async function getRole(id: string): Promise<Role | null> {
  const snapshot = await firebase
    .firestore()
    .collection(roleCollectionKey)
    .doc(id)
    .get();
  const data = snapshot.data();
  if (!snapshot.exists || !data) {
    return null;
  }
  return convertToRole(data, snapshot.ref);
}

export function getEmail(): string {
  const user = firebase.auth().currentUser;
  if (!user) {
    return "";
  }

  return user.email ?? "";
}

export function isVerifiedEmail(): boolean {
  const user = firebase.auth().currentUser;
  if (!user) {
    return false;
  }
  return user.email !== null && user.emailVerified;
}

export async function updateEmail(email: string): Promise<void> {
  const user = firebase.auth().currentUser;
  if (!user) {
    throw new Error("ユーザー情報の取得に失敗しました");
  }

  await user.updateEmail(email);
}

export async function sendEmailVerification(): Promise<void> {
  const user = firebase.auth().currentUser;
  if (!user) {
    throw new Error("ユーザー情報の取得に失敗しました");
  }
  await user.sendEmailVerification();
}

export async function sendResetPasswordEmail(email: string): Promise<void> {
  await firebase.auth().sendPasswordResetEmail(email);
}

export async function adminSignInWithExternalProvider(
  provider: "google" | "microsoft",
  code: string,
  redirectUri: string
) {
  const adminLogin = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("admin_login");
  const res = await adminLogin({
    externalProvider: { provider, code, redirectUri }
  });

  const data = res.data as { token: string };
  if (!data.token) {
    throw new Error("token is empty");
  }

  const userCredential = await firebase
    .auth()
    .signInWithCustomToken(data.token);

  if (!userCredential.user) {
    throw new Error("user not found");
  }

  await assertAdminAccessAllowed(userCredential.user.uid);
  markStaffLoginViaSso();
}
